import React from "react";

import { Helmet } from "react-helmet";

import RillDashboard from "manager/components/RillDashboard";
import { useGetRillDashboard } from "manager/hooks/api/rillQueries";
import Container from "shared/components/Container";
import LargeLoader from "shared/components/LargeLoader";
import { BackLink } from "shared/components/Links";
import PageTitle from "shared/components/PageTitle";
import { PERMISSIONS } from "shared/config/authConstants";
import { usePermissions } from "shared/hooks";
import { BackLinkProps } from "shared/interfaces/misc";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { getCompanyOverviewDashboardResource } from "./Analytics.config";

interface AnalyticsProps {
  backLink: BackLinkProps;
}

const Analytics: React.FC<AnalyticsProps> = ({ backLink }) => {
  const useRillAnalytics = useVeroFlag(VeroFlag.useRillAnalytics);
  const { hasPermission } = usePermissions();
  const canViewAnalytics =
    useRillAnalytics && hasPermission(PERMISSIONS.CAN_VIEW_ANALYTICS);

  const rillDashboardResource = getCompanyOverviewDashboardResource();

  const {
    rillDashboard,
    isRillDashboardLoading,
    rillDashboardError,
    refetchRillDashboard,
  } = useGetRillDashboard(
    {
      resource: rillDashboardResource,
      type: 'canvas',
      navigation: true,
      theme: 'light',
      ttl_seconds: 86400,
    },
    { enabled: canViewAnalytics }
  );

  if (!canViewAnalytics) {
    return null;
  }

  const handleRetry = () => {
    refetchRillDashboard();
  };

  return (
    <>
      <LargeLoader absoluteCenter showLoader={isRillDashboardLoading} />
      <Helmet>
        <title>Analytics</title>
      </Helmet>
      {!isRillDashboardLoading && (
        <>
          <Container noPaddingBottom>
            <BackLink.Container>
              <BackLink {...backLink} />
            </BackLink.Container>
            <PageTitle.Container>
              <PageTitle>Analytics</PageTitle>
            </PageTitle.Container>
          </Container>

          <Container noPaddingTop noMobilePaddingX>
            {rillDashboardError ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <p style={{ color: "#ff4d4f" }}>
                  Error loading analytics:{" "}
                  {rillDashboardError?.response?.data?.error || "Unknown error"}
                </p>
                <button
                  onClick={handleRetry}
                  type="button"
                  style={{
                    marginTop: "10px",
                    padding: "8px 16px",
                    backgroundColor: "#1890ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : rillDashboard ? (
              <RillDashboard
                iframeSrc={rillDashboard.iframeSrc}
                title="Analytics Dashboard"
              />
            ) : (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <p>No dashboard data available</p>
              </div>
            )}
          </Container>
        </>
      )}
    </>
  );
};

export default Analytics;
